import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../ui/Modal';
import Spinner from '../ui/Spinner';
import ChannelSelector from './ChannelSelector';
import { cn } from '../ui/Kit';
import { api } from '../../lib/api';
import { useToast } from '../ui/Toast';
import { LEAD_TYPE_FORM_OPTIONS } from '../../lib/leadMeta';
import {
  getActiveDirectoryUsers,
  loadUsersDirectory,
} from '../../lib/usersDirectory';

const STATUS_OPTIONS = [
  { label: 'New', value: 'new' },
  { label: 'Contacted', value: 'contacted' },
  { label: 'Follow Up', value: 'follow_up' },
  { label: 'Appointment Set', value: 'appointment_set' },
  { label: 'Closed Won', value: 'closed_won' },
  { label: 'Closed Lost', value: 'closed_lost' },
  { label: 'Do Not Contact', value: 'dnc' },
];

const CLOSER_ROLES = new Set(['closer', 'manager', 'admin', 'super_admin']);

function userOptionLabel(u) {
  return u.full_name || 'Unknown';
}

function FieldGroup({ enabled, onToggle, title, children }) {
  return (
    <section
      className={cn(
        'rounded-lg border border-gray-700/60 bg-[#1a2535] p-4 transition-opacity',
        !enabled && 'opacity-50',
      )}
    >
      <label className="flex items-center gap-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onToggle(e.target.checked)}
          className="rounded border-gray-500 text-[#00AAFF] focus:ring-[#00AAFF] bg-[#0F1923]"
        />
        <span className="text-sm font-semibold text-white">{title}</span>
      </label>
      {enabled && <div className="mt-4 space-y-3">{children}</div>}
    </section>
  );
}

function DarkSelect({ label, value, onChange, options, disabled }) {
  return (
    <div>
      {label && (
        <label className="block text-xs font-medium text-gray-400 mb-1">{label}</label>
      )}
      <select
        value={value}
        onChange={onChange}
        disabled={disabled}
        className="flex h-10 w-full rounded-md border border-gray-600 bg-[#0F1923] px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#00AAFF] disabled:opacity-50"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function BatchEditModal({ selectedIds, onClose, onSuccess, agencyId }) {
  const { addToast } = useToast();
  const count = selectedIds?.length ?? 0;

  const [roster, setRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [enableExclusivity, setEnableExclusivity] = useState(false);
  const [exclusivityStatus, setExclusivityStatus] = useState('exclusive');
  const [exclusiveAgentId, setExclusiveAgentId] = useState('');
  const [exclusivityExpiresAt, setExclusivityExpiresAt] = useState('');

  const [enableStatus, setEnableStatus] = useState(false);
  const [status, setStatus] = useState('new');

  const [enableChannel, setEnableChannel] = useState(false);
  const [channel, setChannel] = useState('IM');

  const [enableAssignments, setEnableAssignments] = useState(false);
  const [setterId, setSetterId] = useState('');
  const [closerId, setCloserId] = useState('');

  const [enableLeadType, setEnableLeadType] = useState(false);
  const [leadType, setLeadType] = useState('fresh');

  const [enableOwnership, setEnableOwnership] = useState(false);
  const [ownershipAgentId, setOwnershipAgentId] = useState('');

  useEffect(() => {
    let cancelled = false;
    setRosterLoading(true);
    (async () => {
      try {
        const users = await loadUsersDirectory();
        if (!cancelled) {
          setRoster(getActiveDirectoryUsers(users));
        }
      } catch (err) {
        if (!cancelled) {
          addToast({ type: 'error', title: 'Could not load team', message: err.message });
          setRoster([]);
        }
      } finally {
        if (!cancelled) setRosterLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [agencyId, addToast]);

  const setterOptions = useMemo(
    () => [
      { label: 'None', value: '' },
      ...roster
        .filter((u) => u.role === 'setter' || u.role === 'associate')
        .map((u) => ({ value: u.id, label: userOptionLabel(u) })),
    ],
    [roster],
  );

  const closerOptions = useMemo(
    () => [
      { label: 'None', value: '' },
      ...roster
        .filter((u) => CLOSER_ROLES.has(u.role))
        .map((u) => ({ value: u.id, label: userOptionLabel(u) })),
    ],
    [roster],
  );

  const agentOptions = useMemo(
    () => [
      { label: '— Select agent —', value: '' },
      ...roster.map((u) => ({ value: u.id, label: userOptionLabel(u) })),
    ],
    [roster],
  );

  const buildUpdates = () => {
    const updates = {};

    if (enableExclusivity) {
      updates.exclusivity_status = exclusivityStatus;
      if (exclusivityStatus === 'non_exclusive') {
        updates.exclusive_to = null;
        updates.exclusivity_expires_at = null;
      } else {
        if (exclusiveAgentId) updates.exclusive_to = exclusiveAgentId;
        if (exclusivityExpiresAt) {
          updates.exclusivity_expires_at = new Date(`${exclusivityExpiresAt}T23:59:59`).toISOString();
        }
      }
    }

    if (enableStatus) updates.status = status;
    if (enableChannel) updates.channel = channel;
    if (enableLeadType) updates.lead_type = leadType;

    if (enableOwnership) {
      if (!ownershipAgentId) return null;
      updates.exclusive_to = ownershipAgentId;
      updates.exclusivity_status = 'exclusive';
    }

    return updates;
  };

  const hasAnyToggle =
    enableExclusivity ||
    enableStatus ||
    enableChannel ||
    enableAssignments ||
    enableLeadType ||
    enableOwnership;

  const handleSave = async () => {
    if (!hasAnyToggle) {
      addToast({
        type: 'error',
        title: 'Nothing to update',
        message: 'Enable at least one field group to apply changes.',
      });
      return;
    }

    if (enableOwnership && !ownershipAgentId) {
      addToast({
        type: 'error',
        title: 'Owner required',
        message: 'Select an agent for ownership.',
      });
      return;
    }

    const updates = buildUpdates();
    if (updates === null) return;

    const hasUpdates = updates && Object.keys(updates).length > 0;

    setSaving(true);
    try {
      for (const leadId of selectedIds) {
        if (enableAssignments) {
          await api.leads.setAssignments(leadId, {
            setterUserId: setterId || null,
            closerUserId: closerId || null,
          });
        }
        if (hasUpdates) {
          await api.leads.update(leadId, updates);
        }
      }
      addToast({
        type: 'success',
        title: `${count} lead${count === 1 ? '' : 's'} updated`,
      });
      onSuccess?.();
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Batch update failed',
        message: err.message || 'Could not update leads.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={() => !saving && onClose()}
      title={`Batch Edit — ${count} Lead${count === 1 ? '' : 's'}`}
      className="max-w-2xl bg-[#0F1923] text-white border border-gray-700/50 p-0 overflow-hidden [&_h2]:text-white [&_h2]:px-6 [&_h2]:pt-6 [&_h2]:mb-0"
    >
      <div className="overflow-y-auto max-h-[80vh] px-6 pb-6 space-y-4">
        <p className="text-sm text-gray-400 italic pt-2">
          Only fields you change will be updated. Leave a field at its default to skip it.
        </p>

        {rosterLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Spinner className="text-[#00AAFF]" />
            Loading team…
          </div>
        )}

        <FieldGroup enabled={enableExclusivity} onToggle={setEnableExclusivity} title="Exclusivity">
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-200 cursor-pointer">
              <input
                type="radio"
                name="batch-exclusivity"
                checked={exclusivityStatus === 'exclusive'}
                onChange={() => setExclusivityStatus('exclusive')}
                className="text-[#00AAFF] focus:ring-[#00AAFF]"
              />
              Exclusive
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-200 cursor-pointer">
              <input
                type="radio"
                name="batch-exclusivity"
                checked={exclusivityStatus === 'non_exclusive'}
                onChange={() => setExclusivityStatus('non_exclusive')}
                className="text-[#00AAFF] focus:ring-[#00AAFF]"
              />
              Non-exclusive
            </label>
          </div>
          {exclusivityStatus === 'exclusive' && (
            <>
              <DarkSelect
                label="Assign Exclusive To"
                value={exclusiveAgentId}
                onChange={(e) => setExclusiveAgentId(e.target.value)}
                options={agentOptions}
                disabled={rosterLoading || saving}
              />
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">
                  Exclusivity expires (optional)
                </label>
                <input
                  type="date"
                  value={exclusivityExpiresAt}
                  onChange={(e) => setExclusivityExpiresAt(e.target.value)}
                  disabled={saving}
                  className="flex h-10 w-full rounded-md border border-gray-600 bg-[#0F1923] px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#00AAFF]"
                />
              </div>
            </>
          )}
        </FieldGroup>

        <FieldGroup enabled={enableStatus} onToggle={setEnableStatus} title="Status">
          <DarkSelect
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={STATUS_OPTIONS}
            disabled={saving}
          />
        </FieldGroup>

        <FieldGroup enabled={enableChannel} onToggle={setEnableChannel} title="Channel">
          <ChannelSelector value={channel} onChange={setChannel} disabled={saving} size="sm" />
        </FieldGroup>

        <FieldGroup enabled={enableAssignments} onToggle={setEnableAssignments} title="Agent Assignment">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DarkSelect
              label="Setter"
              value={setterId}
              onChange={(e) => setSetterId(e.target.value)}
              options={setterOptions}
              disabled={rosterLoading || saving}
            />
            <DarkSelect
              label="Closer"
              value={closerId}
              onChange={(e) => setCloserId(e.target.value)}
              options={closerOptions}
              disabled={rosterLoading || saving}
            />
          </div>
        </FieldGroup>

        <FieldGroup enabled={enableLeadType} onToggle={setEnableLeadType} title="Lead Type">
          <DarkSelect
            value={leadType}
            onChange={(e) => setLeadType(e.target.value)}
            options={LEAD_TYPE_FORM_OPTIONS}
            disabled={saving}
          />
        </FieldGroup>

        <FieldGroup enabled={enableOwnership} onToggle={setEnableOwnership} title="Ownership">
          <p className="text-xs text-gray-400 -mt-1 mb-2">
            Sets exclusive owner and marks leads as exclusive.
          </p>
          <DarkSelect
            label="Owner"
            value={ownershipAgentId}
            onChange={(e) => setOwnershipAgentId(e.target.value)}
            options={agentOptions}
            disabled={rosterLoading || saving}
          />
        </FieldGroup>
      </div>

      <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-700/60 bg-[#0F1923]">
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !hasAnyToggle}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#00AAFF] text-white text-sm font-medium hover:bg-[#0099e6] transition-colors disabled:opacity-50"
        >
          {saving ? (
            <>
              <Spinner className="w-4 h-4" />
              Saving…
            </>
          ) : (
            'Save Changes'
          )}
        </button>
      </div>
    </Modal>
  );
}
